import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  CASH_LIST_CONFIGURATION_KEY,
  CASH_TRANSACTION_GROUP_LIST_KEY,
} from '../cash-management/queryKeys';
import { PAYMENTS_INFO_KEY } from '../payment-phoenix';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import type {
  CashListParams,
  CashListTransactionGroupSingle,
  TransformedCashTransactionGroup,
  CreateCashListTransactionGroupFromPliantParams,
  CreateCashListTransactionGroupParams,
  GetCashListConfigurationResponse,
  GetCashListsResponse,
  GetCashListTransactionGroupParams,
  GetCashListTransactionGroupResponse,
  GetCashListTransactionsParams,
  GetCashListTransactionsResponse,
  ICashManagementPhoenixApi,
  ReassignPliantTransactionParams,
  TransferCashListParams,
  TransferCashListResponse,
} from './api';
import { CashManagementPhoenixApi } from './api';
import { transformPhoenixCashTransactionGroup } from './transformers';
import {
  CASH_LIST_CONFIGURATION_PHOENIX_KEY,
  CASH_LISTS_PHOENIX_KEY,
  CASH_LIST_TRANSACTIONS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
  TRANSFER_KEY,
} from './queryKeys';
import { CashListStorageType } from '@pflegenavi/shared/api';
import { getFinanceKey } from '../reporting-phoenix';

export type {
  CashList,
  CashListConfiguration,
  CashListTransaction,
  CashListTransactionGroup,
  CashListTransactionGroupSingle,
  GetCashListConfigurationResponse,
  GetCashListsResponse,
  GetCashListTransactionGroupResponse,
  GetCashListTransactionsParams,
  GetCashListTransactionsResponse,
  ICashManagementPhoenixApi,
  TransferCashListParams,
  TransferCashListResponse,
  TransformedCashTransactionGroup,
} from './api';

const ApiContext = createContext<ICashManagementPhoenixApi | undefined>(
  undefined
);
const {
  useApi: useCashManagementPhoenixApi,
  ApiProvider: CashManagementPhoenixApiProvider,
} = makeApiProvider({
  name: 'CashManagementPhoenixApi',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new CashManagementPhoenixApi(tenantId, auth, apiUrl),
});

export {
  ApiContext as CashManagementPhoenixApiContext,
  CashManagementPhoenixApiProvider,
  useCashManagementPhoenixApi,
};

export function useCashListTransactionGroupCreate(
  nursingHomeId: string | undefined
): UseMutationResult<
  { id: string },
  unknown,
  CreateCashListTransactionGroupParams
> {
  const api = useCashManagementPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    { id: string },
    unknown,
    CreateCashListTransactionGroupParams
  >({
    mutationKey: ['cash-transaction-group-add-transaction'],
    mutationFn: (data) => {
      if (!api) {
        throw new Error('CashManagementPhoenixApi is not initialized');
      }
      return api.createCashListTransactionGroup(data);
    },
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_CONFIGURATION_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: getFinanceKey(nursingHomeId),
        }),
      ]);
    },
  });
}

export function useTransferCallbackPhoenix(
  nursingHomeId: string | undefined
): (params: TransferCashListParams) => Promise<TransferCashListResponse> {
  const api = useCashManagementPhoenixApi();
  const queryClient = useQueryClient();

  return useCallback(
    async (params: TransferCashListParams) => {
      if (!api) {
        throw new Error('CashManagementPhoenixApi is not initialized');
      }
      const result = await api.transfer(params);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_CONFIGURATION_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSFER_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: getFinanceKey(nursingHomeId),
        }),
      ]);

      return result;
    },
    [api, nursingHomeId, queryClient]
  );
}

export function useCashListTransactionGroupFromPliantCreate(): UseMutationResult<
  { cash_list_transaction_group: CashListTransactionGroupSingle },
  unknown,
  CreateCashListTransactionGroupFromPliantParams
> {
  const api = useCashManagementPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    { cash_list_transaction_group: CashListTransactionGroupSingle },
    unknown,
    CreateCashListTransactionGroupFromPliantParams
  >({
    mutationKey: ['cash-transaction-group-from-pliant-create'],

    mutationFn: api.createFromPliant,
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
}

export function useReassignPliantTransaction(
  nursingHomeId: string | undefined
): UseMutationResult<unknown, unknown, ReassignPliantTransactionParams> {
  const api = useCashManagementPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, ReassignPliantTransactionParams>({
    mutationKey: ['cash-list-reassign-pliant-transaction'],
    mutationFn: (data) => {
      if (!api) {
        throw new Error('CashManagementPhoenixApi is not initialized');
      }
      return api.reassignPliantTransaction(data);
    },
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_CONFIGURATION_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: getFinanceKey(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
}

export function useCashListTransactionGroupPhoenix(
  params: GetCashListTransactionGroupParams['params'],
  options?: Omit<
    UseQueryOptions<GetCashListTransactionGroupResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetCashListTransactionGroupResponse, unknown> {
  const select = useCallback((data: GetCashListTransactionGroupResponse) => {
    const cashTransactionGroupList = data.cash_transaction_group_list.map(
      (group) => ({
        ...group,
        update_date: new Date(group.update_date),
        initial_pliant_transaction_created_at:
          group.initial_pliant_transaction_created_at
            ? new Date(group.initial_pliant_transaction_created_at)
            : group.initial_pliant_transaction_created_at,
      })
    );

    return {
      ...data,
      cash_transaction_group_list: cashTransactionGroupList,
    };
  }, []);

  return useApiQuery(
    useCashManagementPhoenixApi,
    CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(params),
    (api) => {
      return api.getCashListTransactionGroup({
        params,
      });
    },
    {
      select,
      ...options,
    }
  );
}

export function useCashListsPhoenix(
  params?: CashListParams['params'],
  options?: Omit<
    UseQueryOptions<GetCashListsResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetCashListsResponse, unknown> {
  const select = useCallback((data: GetCashListsResponse) => {
    return {
      data: data.data.map((cashList) => ({
        ...cashList,
        storage_type:
          (cashList.supports_bank_account
            ? CashListStorageType.BankAccount
            : 0) | (cashList.supports_cash ? CashListStorageType.Cash : 0),
      })),
    };
  }, []);

  return useApiQuery(
    useCashManagementPhoenixApi,
    CASH_LISTS_PHOENIX_KEY(params),
    (api) => {
      return api.getCashLists({
        params,
      });
    },
    {
      select,
      ...options,
    }
  );
}

export function useCashListConfigurationPhoenix(
  nursingHomeId: string,
  options?: Omit<
    UseQueryOptions<GetCashListConfigurationResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetCashListConfigurationResponse, unknown> {
  return useApiQuery(
    useCashManagementPhoenixApi,
    CASH_LIST_CONFIGURATION_PHOENIX_KEY(nursingHomeId),
    (api) => {
      return api.getCashListConfiguration({
        params: {
          nursing_home_id: nursingHomeId,
        },
      });
    },
    options
  );
}

export function useCashListTransactionsPhoenix(
  params: GetCashListTransactionsParams['params'],
  options?: Omit<
    UseQueryOptions<GetCashListTransactionsResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetCashListTransactionsResponse, unknown> {
  const select = useCallback((data: GetCashListTransactionsResponse) => {
    return data;
  }, []);

  return useApiQuery(
    useCashManagementPhoenixApi,
    CASH_LIST_TRANSACTIONS_PHOENIX_KEY(
      params.cash_list_id,
      params.page,
      params.page_size,
      params.date_from,
      params.date_to
    ),
    (api) => {
      return api.getCashListTransactions({
        params,
      });
    },
    {
      select,
      ...options,
    }
  );
}

export function useCashTransactionGroupPhoenix(
  groupId?: string,
  enabled = true
): UseQueryResult<TransformedCashTransactionGroup, unknown> {
  const select = useCallback(
    (data: { cash_list_transaction_group: CashListTransactionGroupSingle }) =>
      transformPhoenixCashTransactionGroup(data),
    []
  );

  return useApiQuery(
    useCashManagementPhoenixApi,
    CASH_TRANSACTION_GROUP_PHOENIX_KEY(groupId),
    (api) => {
      if (!groupId) {
        throw new Error('groupId is required');
      }
      return api.getCashTransactionGroup({ params: { groupId } });
    },
    {
      enabled: groupId !== undefined && enabled,
      select,
    }
  );
}

// Suspense version that doesn't transform the data
export function useCashTransactionGroupSuspense(
  groupId: string
): UseSuspenseQueryResult<
  { cash_list_transaction_group: CashListTransactionGroupSingle },
  unknown
> {
  const api = useCashManagementPhoenixApi();

  return useSuspenseQuery({
    queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(groupId),
    queryFn: () => api.getCashTransactionGroup({ params: { groupId } }),
  });
}
